import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  History, Sparkles, Bug, Wrench, Calendar, 
  ChevronDown, ChevronUp, Info, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const VersionPage = () => {
  const navigate = useNavigate();
  const [changelog, setChangelog] = useState([]);
  const [currentVersion, setCurrentVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedVersions, setExpandedVersions] = useState(new Set(['0'])); // First one expanded by default

  useEffect(() => {
    loadChangelog();
  }, []);

  const loadChangelog = async () => {
    try {
      const response = await api.get('/changelog');
      setChangelog(response.data.changelog || []);
      setCurrentVersion(response.data.current_version || '');
    } catch (error) {
      console.error('Error loading changelog:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleVersion = (index) => {
    const newExpanded = new Set(expandedVersions);
    if (newExpanded.has(index.toString())) {
      newExpanded.delete(index.toString());
    } else {
      newExpanded.add(index.toString());
    }
    setExpandedVersions(newExpanded);
  };

  const getChangeIcon = (type) => {
    switch (type) {
      case 'feature':
        return <Sparkles className="h-4 w-4 text-green-500" />;
      case 'fix':
        return <Bug className="h-4 w-4 text-red-500" />;
      case 'improvement':
        return <Wrench className="h-4 w-4 text-blue-500" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  const getChangeBadge = (type) => {
    switch (type) {
      case 'feature':
        return <Badge className="bg-green-500/15 text-green-400 border-green-500/30 text-xs whitespace-nowrap">Nueva función</Badge>;
      case 'fix':
        return <Badge className="bg-red-500/15 text-red-400 border-red-500/30 text-xs whitespace-nowrap">Corrección</Badge>;
      case 'improvement':
        return <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/30 text-xs whitespace-nowrap">Mejora</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs whitespace-nowrap">Otro</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <History className="h-6 w-6 text-primary" />
              Historial de Versiones
            </h1>
            <p className="text-muted-foreground text-sm">
              Novedades y actualizaciones del sistema
            </p>
          </div>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          v{currentVersion}
        </Badge>
      </div>

      {/* Current Version Card */}
      {changelog[0] && (
        <Card className="border-primary/50 bg-primary/5">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Versión Actual: {changelog[0].version}</CardTitle>
              </div>
              <Badge className="bg-primary text-primary-foreground">Última</Badge>
            </div>
            <CardDescription className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {formatDate(changelog[0].date)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="font-medium mb-3">{changelog[0].title}</p>
            <div className="space-y-2">
              {changelog[0].changes.map((change, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  {getChangeIcon(change.type)}
                  <span className="flex-1">{change.text}</span>
                  {getChangeBadge(change.type)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Previous Versions */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-muted-foreground">Versiones Anteriores</h2>
        
        {changelog.slice(1).map((version, index) => (
          <Card key={version.version} className="overflow-hidden">
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => toggleVersion(index + 1)}
            >
              <div className="flex items-center gap-3">
                <Badge variant="outline">{version.version}</Badge>
                <span className="font-medium">{version.title}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">{formatDate(version.date)}</span>
                {expandedVersions.has((index + 1).toString()) ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </div>
            </div>
            
            {expandedVersions.has((index + 1).toString()) && (
              <CardContent className="pt-0 border-t">
                <div className="space-y-2 pt-4">
                  {version.changes.map((change, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      {getChangeIcon(change.type)}
                      <span className="flex-1">{change.text}</span>
                      {getChangeBadge(change.type)}
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-muted-foreground pt-4">
        <p>CM Idiomas - Sistema de Gestión de Inventario</p>
        <p>© 2026 Todos los derechos reservados</p>
      </div>
    </div>
  );
};

export default VersionPage;
